import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Activity, ChevronLeft, Plus, Trash2, Download, Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { useAuth } from '../../contexts/AuthContext';
import Navbar from '../../components/Navbar';
import { toast } from 'sonner';

export default function AdminSpecialties() {
  const { api } = useAuth();
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    fetchSpecialties();
  }, []);

  const fetchSpecialties = async () => {
    try {
      const response = await api.get('/admin/specialties');
      setSpecialties(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) {
      toast.error('Informe o nome da especialidade');
      return;
    }

    try {
      await api.post('/admin/specialties', { name: newName, description: newDescription });
      toast.success('Especialidade criada');
      setDialogOpen(false);
      setNewName('');
      setNewDescription('');
      fetchSpecialties();
    } catch (error) {
      toast.error('Erro ao criar especialidade');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover esta especialidade?')) return;

    try {
      await api.delete(`/admin/specialties/${id}`);
      toast.success('Especialidade removida');
      fetchSpecialties();
    } catch (error) {
      toast.error('Erro ao remover especialidade');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-specialties">
      <Navbar />

      <div className="container-app py-8">
        <Link to="/admin" className="inline-flex items-center gap-2 text-slate-600 hover:text-primary mb-6">
          <ChevronLeft className="w-4 h-4" />
          Voltar
        </Link>

        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900">Especialidades</h1>
            <p className="text-slate-600 mt-1">{!loading && `${specialties.length} especialidades cadastradas`}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={async () => {
                setDownloading(true);
                try {
                  const res = await api.get('/admin/specialties/download-excel', { responseType: 'blob' });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = 'especialidades.xlsx';
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  window.URL.revokeObjectURL(url);
                  toast.success('Download concluido!');
                } catch { toast.error('Erro ao gerar Excel'); }
                finally { setDownloading(false); }
              }}
              disabled={downloading}
              data-testid="download-specialties-btn"
            >
              {downloading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Excel
            </Button>
            <Button onClick={() => setDialogOpen(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Nova Especialidade
            </Button>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-4">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={`skeleton-${i}`} className="animate-pulse h-24 bg-white rounded-2xl"></div>
            ))
          ) : specialties.length > 0 ? (
            specialties.map((specialty) => (
              <div key={specialty.id} className="bg-white rounded-2xl border border-slate-100 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Activity className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-900">{specialty.name}</p>
                    <p className="text-xs text-slate-500">{specialty.slug}</p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className="text-red-500 hover:text-red-600"
                  onClick={() => handleDelete(specialty.id)}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))
          ) : (
            <div className="col-span-3 text-center py-16 bg-white rounded-2xl">
              <Activity className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhuma especialidade encontrada</p>
            </div>
          )}
        </div>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Nova Especialidade</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="Ex: Cardiologia"
              />
            </div>
            <div className="space-y-2">
              <Label>Descrição (opcional)</Label>
              <Input
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Descrição da especialidade"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleCreate} className="bg-primary">
              Criar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
